import request from '../request';
import services from '../../services';
import { CLEAN, GMI, STATE } from '../../constants';
import { fromJS } from 'immutable';
import { types } from '../../utils/js/types';
import { isObjectEmpty } from '../../utils/js/object';
import { batchActions } from '../../utils/redux';

/**
 * @typedef {object} LoyaltyModifyType
 * @property {string} loyalty_number
 */
/**
 * @typedef {object} ContactModifyType
 * @property {object} contact contact profile type
 * @property {string} contact.mask_email: string,
 * @property {object[]} contact.phones: object[]
 */
/**
 * @typedef {object} AddressModifyType
 * @property {object} address address modify type
 * @property {string[]} address.street_addresses
 * @property {string} address.city
 * @property {string} address.country_subdivision_code
 * @property {string} address.country_subdivision_name
 * @property {string} address.country_name
 * @property {string} address.postal
 */
/**
 * @typedef {object} LicenseModifyType
 * @property {object} drivers_license license modify type
 * @property {string} drivers_license.country_code
 * @property {string} drivers_license.country_subdivision_code
 * @property {string} drivers_license.do_not_rent_indicator
 * @property {string} drivers_license.country_subdivision_name
 * @property {string} drivers_license.birth_date
 * @property {string} drivers_license.country_name
 * @property {string} drivers_license.license_expiration_date
 * @property {string} drivers_license.license_number
 */

/**
 * @typedef {object} ProfileModifyArgsType
 * @property {LoyaltyModifyType} loyaltyState
 * @property {ContactModifyType} contactState
 * @property {AddressModifyType} addressState
 * @property {LicenseModifyType} licenseState
 */

/**
 * @typedef {object} RequestModes
 * @property {boolean} update indicates whether the update mode is enabled.
 */

/**
 * @typedef {object} ProfileModifyRequestOptsType
 * @property {RequestModes} modes
 */

const { PATH: PROFILE } = STATE.UI.PROFILE.MODIFY;

// For setUIProfileModifyState
const batchedActions = [];

const LOYALTY_NUMBER_TYPE = 'loyalty_number';
const ADDRESS_TYPE = 'address';
const CONTACT_TYPE = 'contact';
const DRIVERS_TYPE = `drivers_license`;

/**
 * Paths from the gmi.ui.profile state to various properties
 * @type       {object}
 * @memberof actions.profile
 */
export const profileModifyPaths = {
  loyalty_number: PROFILE.concat([LOYALTY_NUMBER_TYPE]),
  address: PROFILE.concat([ADDRESS_TYPE]),
  contact: PROFILE.concat([CONTACT_TYPE]),
  date_of_birth: PROFILE.concat(['date_of_birth']),
  drivers_license: PROFILE.concat([DRIVERS_TYPE]),
  payment: PROFILE.concat(['payment'])
};

/**
 * Base path from session to gbo.profile
 * @type       {string[]}
 * @private
 */
const GBO_PROFILE = ['gbo', 'profile'];

/**
 * Base paths to various `gbo.profile` properties
 * @memberof actions.profile
 */
export const gboProfilePaths = {
  /** @desc `gbo.profile.profile.basic_profile.loyalty_data.loyalty_number` */
  userLoyaltyIdPath: GBO_PROFILE.concat([
    'profile',
    'basic_profile',
    'loyalty_data',
    'loyalty_number'
  ]),
  /** @desc `gbo.profile.profile.basic_profile.date_of_birth` */
  userDateOfBirth: GBO_PROFILE.concat(['profile.basic_profile', 'date_of_birth']),
  /** @desc `gbo.profile.contact_profile` */
  userContactPath: GBO_PROFILE.concat(['contact_profile']),
  /** @desc `gbo.profile.address_profile` */
  userAddressPath: GBO_PROFILE.concat(['address_profile']),
  /** @desc `gbo.profile.license_profile */
  userLicensePath: GBO_PROFILE.concat(['license_profile'])
};

/**
 * @name cleanNonSessionProfileState
 * @desc Removes data from store not associated with session.gbo.profile; i.e. gma.trips.current
 * @returns {Object}
 * @memberof actions.profile
 * @example
 * // restore state.gmi.gma.trips and state.gmi.gma.rewards to initialState
 * dispatch(actions.profile.cleanNonSessionProfileState());
 */
export function cleanNonSessionProfileState() {
  return { type: CLEAN.CLEAN_PROFILE_STATE };
}

/**
 * @memberof actions.profile.modify
 * @name setUIProfileModifyState
 * @desc sets the correct data into the correct path for ui.profile.modify
 * @param {ProfileModifyArgsType} [opts={}]
 * @returns {Function}
 *
 * @example
 * dispatch(setUIProfileModifyState({loyaltyState})) // sets only loyalty state
 */
export function setUIProfileModifyState(opts = {}) {
  return dispatch => {
    if (opts.loyaltyState && !isObjectEmpty(opts.loyaltyState)) {
      batchedActions.push(setProfileLoyaltyState(opts.loyaltyState));
    }
    if (opts.contactState && !isObjectEmpty(opts.contactState)) {
      batchedActions.push(setProfileContactState(opts.contactState));
    }
    if (opts.addressState && !isObjectEmpty(opts.addressState)) {
      batchedActions.push(setProfileAddressState(opts.addressState));
    }
    if (opts.licenseState && !isObjectEmpty(opts.licenseState)) {
      batchedActions.push(setProfileLicenseState(opts.licenseState));
      return dispatch(batchActions(...batchedActions));
    }
  };
}

/**
 * @name hasProfile
 * @desc Determines if the `gbo.profile` exists
 * @param      {object}   session  The session
 * @return     {boolean}  True if profile, False otherwise.
 * @private
 */
function hasProfile(session) {
  return !!session.getIn(GBO_PROFILE, null);
}

/**
 * @name getProfileLoyaltyState
 * @desc Gets the loyalty-related data for the profile/modify endpoint
 * @param {object} session session/current response
 * @returns {LoyaltyModifyType} object with loyalty string
 * @memberof actions.profile.modify
 */
export function getProfileLoyaltyState(session) {
  if (hasProfile(session)) {
    const userLoyaltyId = session.getIn(gboProfilePaths.userLoyaltyIdPath, null);
    if (userLoyaltyId) {
      return {
        loyalty_number: userLoyaltyId
      };
    }
  }
  return {};
}

/**
 * @name getProfileContactState
 * @desc Gets the profile contact state.
 * @param      {object}  session  The session/current response
 * @return     {ContactModifyType}  The profile contact state
 * @memberof actions.profile.modify
 */
export function getProfileContactState(session) {
  if (hasProfile(session)) {
    const contactProfile = session.getIn(gboProfilePaths.userContactPath, null);

    if (contactProfile) {
      return {
        contact: contactProfile.toJS()
      };
    }
  }
  return {};
}

/**
 * @name getProfileAddressState
 * @desc Gets the profile address state.
 * @param      {object}  session  The session/current response
 * @return     {AddressModifyType}  The profile address state.
 * @memberof actions.profile.modify
 */
export function getProfileAddressState(session) {
  if (hasProfile(session)) {
    const addressProfile = session.getIn(gboProfilePaths.userAddressPath, null);
    if (addressProfile) {
      return {
        address: addressProfile.toJS()
      };
    }
  }
  return {};
}

/**
 * @name getProfileLicenseState
 * @desc Gets the profile license state.
 * @param      {object}  session  The session/current response
 * @return     {LicenseModifyType}  The profile license state.
 * @memberof actions.profile.modify
 */
export function getProfileLicenseState(session) {
  if (hasProfile(session)) {
    const licenseProfile = session.getIn(gboProfilePaths.userLicensePath, null);
    if (licenseProfile) {
      return {
        drivers_license: licenseProfile.toJS()
      };
    }
  }
  return {};
}

/**
 * @name setProfileModifyState
 * @desc Sets the profile modify state.
 * @param      {object}  args    The arguments
 * @return     {Function} effectively just dispatches setUIProfileModifyState
 * @memberof actions.profile.modify
 */
export function setProfileModifyState(args) {
  return dispatch => {
    dispatch(setUIProfileModifyState(args));
  };
}

/**
 * @name setProfileLoyaltyState
 * @desc Returns the set profile loyalty action
 * @private
 * @param      {LoyaltyModifyType}  loyaltyState  The loyalty state
 * @return     {Object}  gmi redux action
 * @memberof actions.profile.modify
 */
export function setProfileLoyaltyState(loyaltyState) {
  return {
    type: GMI.SET_GMI_STATE,
    path: profileModifyPaths.loyalty_number,
    value: loyaltyState.loyalty_number
  };
}

/**
 * @name clearProfileLoyaltyState
 * @desc Retuns the clear profile loyalty number action
 * @returns {object} gmi redux action
 * @memberof actions.profile.modify
 */
export function clearProfileLoyaltyState() {
  return { type: GMI.SET_GMI_STATE, path: profileModifyPaths.loyalty_number };
}

/**
 * @name setProfileContactState
 * @desc Returns the set profile contact action
 * @param      {ContactModifyType}  contactState  The contact state
 * @returns     {Object}  gmi redux action
 * @memberof actions.profile.modify
 */
export function setProfileContactState(contactState) {
  return { type: GMI.SET_GMI_STATE, path: profileModifyPaths.contact, value: contactState.contact };
}
/**
 * @name clearProfileContactState
 * @desc Returns the clear profile contact action
 * @returns {object} gmi redux action
 * @memberof actions.profile.modify
 */
export function clearProfileContactState() {
  return { type: GMI.SET_GMI_STATE, path: profileModifyPaths.contact };
}
/**
 * @name setProfileAddressState
 * @desc Returns the set profile address action
 * @param      {AddressModifyType}  addressState  The address state
 * @returns     {Object}  gmi redux action
 * @memberof actions.profile.modify
 */
export function setProfileAddressState(addressState) {
  return { type: GMI.SET_GMI_STATE, path: profileModifyPaths.address, value: addressState.address };
}
/**
 * @name clearProfileAddressState
 * @desc clears the profile address state
 * @returns {object} gmi redux action
 * @memberof actions.profile.modify
 */
export function clearProfileAddressState() {
  return { type: GMI.SET_GMI_STATE, path: profileModifyPaths.address };
}
/**
 * @name setProfileLicenseState
 * @desc Returns the set profile license action
 * @param      {LicenseModifyType}  licenseState  The license state
 * @returns     {Object}  gmi redux action
 * @memberof actions.profile.modify
 */
export function setProfileLicenseState(licenseState) {
  return {
    type: GMI.SET_GMI_STATE,
    path: profileModifyPaths.drivers_license,
    value: licenseState.drivers_license
  };
}
/**
 * @name clearProfileLicenseState
 * @desc clears the profile drivers license action
 * @returns {object} gmi redux action
 * @memberof actions.profile.modify
 */
export function clearProfileLicenseState() {
  return { type: GMI.SET_GMI_STATE, path: profileModifyPaths.drivers_license };
}
/**
 * @name clearProfileModify
 * @desc Clears the whole profile modify ui state
 * @returns {object} gmi redux action
 * @memberof actions.profile.modify
 */
export function clearProfileModify() {
  return { type: GMI.SET_GMI_STATE, path: STATE.UI.PROFILE.MODIFY, value: {} };
}

/**
 * @memberof actions.profile.modify
 * @type {{set:Function, unset:Function}}
 */
export const profileModifyState = {
  /**
   * @name set
   * @desc A global setter for ui.profile.modify state data
   * @param {String} [type]
   * @param {ProfileModifyArgsType} [args={}]
   * @returns {Function}
   * @memberof actions.profile.modify
   * @example
   * // set address profile data
   * store.dispatch(actions.profile.profileModifyState.set('address', {
   *  addressState: { ... }
   * }))
   * // set contact profile data
   * store.dispatch(actions.profile.profileModifyState.set('contact', {
   *  contactState: { ... }
   * }))
   * // set drivers license data
   * store.dispatch(actions.profile.profileModifyState.set('drivers_license', {
   *  licenseState: { ... }
   * }))
   */
  set(type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (type === LOYALTY_NUMBER_TYPE) {
          dispatch(setProfileLoyaltyState(args.loyaltyState));
        } else if (type === ADDRESS_TYPE) {
          dispatch(setProfileAddressState(args.addressState));
        } else if (type === CONTACT_TYPE) {
          dispatch(setProfileContactState(args.contactState));
        } else if (type === DRIVERS_TYPE) {
          dispatch(setProfileLicenseState(args.licenseState));
        }
      } else {
        dispatch(setProfileModifyState(args));
      }
    };
  },
  /**
   * @name unset
   * @desc A global unsetter for ui.profile.modify state data
   * @returns {Function}
   * @memberof actions.profile.modify
   * @example
   * // clear the whole state
   * dispatch(actions.profile.profileModifyState.unset())
   */
  unset(type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (type === LOYALTY_NUMBER_TYPE) {
          dispatch(clearProfileLoyaltyState());
        } else if (type === ADDRESS_TYPE) {
          dispatch(clearProfileAddressState());
        } else if (type === CONTACT_TYPE) {
          dispatch(clearProfileContactState());
        } else if (type === DRIVERS_TYPE) {
          dispatch(clearProfileLicenseState());
        }
      } else {
        dispatch(clearProfileModify());
      }
    };
  }
};

/**
 * @name hydrateUIProfileModifyState
 * @desc hydrates the complete `gmi.ui.profile.modify` source of truth state from `gbo.profile`
 * @param      {object}  [opts={}]  The options for the hydration
 * @returns {Function}
 * @memberof actions.profile.modify
 * @example
 * // hydrate the gmi.ui.profile.modify data from session state
 * store.dispatch(actions.profile.hydrateUIProfileModifyState());
 * // hdrate by passing a session to the action
 * store.dispatch(actions.profile.hydrateUIProfileModifyState(actionSession: {...}));
 */
export function hydrateUIProfileModifyState(opts = {}) {
  const { actionSession } = opts;
  return (dispatch, getState) => {
    const state = getState();
    const session = fromJS(actionSession) || state.getIn(STATE.SESSION);
    const loyaltyState = getProfileLoyaltyState(session);
    const contactState = getProfileContactState(session);
    const addressState = getProfileAddressState(session);
    const licenseState = getProfileLicenseState(session);

    return dispatch(
      setUIProfileModifyState({
        loyaltyState,
        addressState,
        contactState,
        licenseState
      })
    );
  };
}

/**
 * @name profileModify
 * @desc Modifies profile data for the current user in the GMA service
 * @param {ProfileModifyArgsType} data
 * @param {ProfileModifyRequestOptsType} requestOpts
 * @returns {Function}
 * @memberof actions.profile
 * @example
 * dispatch(actions.profile.profileModify(opts));
 */
export function profileModify(data, requestOpts = {}) {
  return (dispatch, getState) => {
    const profileData = getState().getIn(PROFILE)?.toJS();

    const payload = {
      ...profileData,
      ...data
    };

    return dispatch(
      request({
        data: { payload, opts: requestOpts },
        service: services.gma.profile.modify
      })
    );
  };
}
